"use client"

import { useState, useMemo } from "react"
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isSameMonth, isSameDay, setYear, setMonth, getYear, getMonth } from "date-fns"
import { arMA, fr } from "date-fns/locale"
import { ChevronLeft, ChevronRight, CalendarIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useTranslation } from "react-i18next"

interface DatePickerProps {
  date: Date | undefined
  setDate: (date: Date | undefined) => void
  placeholder?: string
  className?: string
  maxDate?: Date
  /** If true, shows year/month dropdowns for easier navigation (useful for birth dates) */
  showYearNavigation?: boolean
}

export function DatePicker({ date, setDate, placeholder, className, maxDate, showYearNavigation = true }: DatePickerProps) {
  const [open, setOpen] = useState(false)
  const [currentMonth, setCurrentMonth] = useState(date || new Date())
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'
  const locale = isRTL ? arMA : fr

  const weekDays = isRTL
    ? ['أحد', 'إثن', 'ثلا', 'أرب', 'خمي', 'جمع', 'سبت']
    : ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di']

  // Generate years for dropdown (100 years back from maxDate or current year)
  const years = useMemo(() => {
    const maxYear = maxDate ? getYear(maxDate) : getYear(new Date())
    const minYear = maxYear - 100
    const yearsList = []
    for (let y = maxYear; y >= minYear; y--) {
      yearsList.push(y)
    }
    return yearsList
  }, [maxDate])

  // Months for dropdown
  const months = useMemo(() => {
    const monthNames = isRTL
      ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'ماي', 'يونيو', 'يوليوز', 'غشت', 'شتنبر', 'أكتوبر', 'نونبر', 'دجنبر']
      : ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
    return monthNames.map((name, index) => ({ value: index, label: name }))
  }, [isRTL])

  const handleSelect = (day: Date) => {
    setDate(day)
    setOpen(false)
  }

  const handleYearChange = (yearStr: string) => {
    const newDate = setYear(currentMonth, parseInt(yearStr))
    setCurrentMonth(newDate)
  }

  const handleMonthChange = (monthStr: string) => {
    const newDate = setMonth(currentMonth, parseInt(monthStr))
    setCurrentMonth(newDate)
  }

  const renderDays = () => {
    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(monthStart)
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 })
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 })

    const days = []
    let day = startDate

    while (day <= endDate) {
      const currentDay = day
      const isCurrentMonth = isSameMonth(day, monthStart)
      const isSelected = date && isSameDay(day, date)
      const isToday = isSameDay(day, new Date())
      const isDisabled = maxDate ? day > maxDate : false

      days.push(
        <button
          key={day.toISOString()}
          type="button"
          onClick={() => !isDisabled && handleSelect(currentDay)}
          disabled={isDisabled}
          className={cn(
            "h-9 w-9 rounded-md text-sm font-normal transition-colors",
            "hover:bg-accent hover:text-accent-foreground",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            !isCurrentMonth && "text-muted-foreground opacity-50",
            isToday && !isSelected && "bg-accent text-accent-foreground",
            isSelected && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
            isDisabled && "opacity-30 cursor-not-allowed hover:bg-transparent"
          )}
        >
          {format(day, 'd')}
        </button>
      )
      day = addDays(day, 1)
    }

    return days
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          type="button"
          className={cn(
            "w-full justify-between font-normal",
            !date && "text-muted-foreground",
            className
          )}
        >
          {date ? format(date, "dd/MM/yyyy", { locale }) : (placeholder || t("pick_a_date"))}
          <CalendarIcon className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={isRTL ? "end" : "start"}>
        <div className="p-3" dir={isRTL ? "rtl" : "ltr"}>
          {/* Year and Month Dropdowns */}
          {showYearNavigation ? (
            <div className={cn("flex items-center gap-2 mb-4", isRTL && "flex-row-reverse")}>
              <Select value={getMonth(currentMonth).toString()} onValueChange={handleMonthChange} dir={isRTL ? "rtl" : "ltr"}>
                <SelectTrigger className="flex-1 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((month) => (
                    <SelectItem key={month.value} value={month.value.toString()}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={getYear(currentMonth).toString()} onValueChange={handleYearChange} dir={isRTL ? "rtl" : "ltr"}>
                <SelectTrigger className="w-24 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {years.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            /* Simple month navigation header */
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="outline"
                size="icon"
                type="button"
                className="h-7 w-7"
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              >
                {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </Button>
              <span className="text-sm font-medium">
                {format(currentMonth, 'MMMM yyyy', { locale })}
              </span>
              <Button
                variant="outline"
                size="icon"
                type="button"
                className="h-7 w-7"
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              >
                {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            </div>
          )}

          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {weekDays.map((day, index) => (
              <div
                key={index}
                className="h-9 w-9 flex items-center justify-center text-xs font-medium text-muted-foreground"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 gap-1">
            {renderDays()}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
